import { useEffect, useState } from 'react';
import { CreditCard, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { CONTACTO } from '@/lib/brand';
import {
  browserHttpContext,
  digitsOnly,
  etiquetaMarcaTarjeta,
  formatCardNumber,
  fallbackDeviceSession,
  loadOpenPay,
  tokenizeCard,
  textoErrorPago,
  type OpenPayStatic,
} from '@/lib/openpay';
import type { OpenpayTarjeta } from '@/types';
import { PaymentMarks } from '@/components/checkout/PaymentMarks';

interface OpenpayCheckoutProps {
  payload: Record<string, unknown>;
  formValid: boolean;
  loggedIn: boolean;
  error?: string | null;
  onSuccess: (result: { folio: string; id_venta: number }) => void;
  onError: (message: string) => void;
}

const YEARS = Array.from({ length: 12 }, (_, i) => {
  const y = (new Date().getFullYear() + i) % 100;
  return String(y).padStart(2, '0');
});

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

export function OpenpayCheckoutSection({
  payload,
  formValid,
  loggedIn,
  error,
  onSuccess,
  onError,
}: OpenpayCheckoutProps) {
  const [config, setConfig] = useState<{
    enabled: boolean;
    merchantId: string;
    publicKey: string;
    sandbox: boolean;
  } | null>(null);
  const [openpay, setOpenpay] = useState<OpenPayStatic | null>(null);
  const [deviceSessionId, setDeviceSessionId] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [holder, setHolder] = useState('');
  const [number, setNumber] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cargaError, setCargaError] = useState<string | null>(null);
  const [tarjetas, setTarjetas] = useState<OpenpayTarjeta[]>([]);
  const [usarNueva, setUsarNueva] = useState(false);
  const [tarjetaId, setTarjetaId] = useState('');
  const [guardarTarjeta, setGuardarTarjeta] = useState(false);

  useEffect(() => {
    api
      .openpayConfig()
      .then(setConfig)
      .catch(() =>
        setConfig({ enabled: false, merchantId: '', publicKey: '', sandbox: true }),
      );
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      setTarjetas([]);
      setTarjetaId('');
      setUsarNueva(true);
      return;
    }
    api
      .openpayCards()
      .then((lista) => {
        setTarjetas(lista);
        if (lista.length > 0) {
          setTarjetaId(lista[0].id);
          setUsarNueva(false);
        } else {
          setUsarNueva(true);
        }
      })
      .catch(() => {
        setTarjetas([]);
        setUsarNueva(true);
      });
  }, [loggedIn]);

  useEffect(() => {
    if (!config?.enabled || !config.merchantId || !config.publicKey) return;

    setCargaError(null);

    const ready = window.OpenPay;
    if (ready) {
      ready.setId(config.merchantId);
      ready.setApiKey(config.publicKey);
      ready.setSandboxMode(!!config.sandbox);
      setOpenpay(ready);
      return;
    }

    loadOpenPay(config.merchantId, config.publicKey, config.sandbox)
      .then(setOpenpay)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'No se pudo cargar OpenPay.js';
        setCargaError(msg);
      });
  }, [config?.enabled, config?.merchantId, config?.publicKey, config?.sandbox]);

  const mostrarFormularioNueva = !loggedIn || usarNueva || tarjetas.length === 0;

  const handlePay = async () => {
    if (!formValid) {
      onError('Complete todos los campos obligatorios antes de pagar.');
      return;
    }
    if (!config?.merchantId || !config.publicKey) {
      onError('OpenPay no está configurado.');
      return;
    }

    setProcesando(true);
    onError('');

    try {
      const sessionId = deviceSessionId || fallbackDeviceSession();
      if (!deviceSessionId) setDeviceSessionId(sessionId);

      if (!mostrarFormularioNueva && tarjetaId) {
        const result = await api.openpayCharge({
          ...payload,
          card_id: tarjetaId,
          device_session_id: sessionId,
          http_context: browserHttpContext(),
          redirect_url: `${window.location.origin}/checkout`,
        });
        await resolverCargo(result);
        return;
      }

      const cardNumber = digitsOnly(number);
      const cvv2 = digitsOnly(cvv);
      const holderName = holder.trim();

      if (holderName.length < 3) {
        throw new Error('Indique el nombre del tarjetahabiente.');
      }

      let instance = openpay ?? window.OpenPay ?? null;
      if (!instance) {
        instance = await loadOpenPay(config.merchantId, config.publicKey, config.sandbox);
      } else {
        instance.setId(config.merchantId);
        instance.setApiKey(config.publicKey);
        instance.setSandboxMode(!!config.sandbox);
      }
      setOpenpay(instance);

      if (!instance.card.validateCardNumber(cardNumber)) {
        throw new Error('El número de tarjeta no es válido.');
      }
      if (!instance.card.validateExpiry(month, year)) {
        throw new Error('La fecha de vencimiento no es válida.');
      }
      if (!instance.card.validateCVC(cvv2, cardNumber)) {
        throw new Error('El código CVV no es válido.');
      }

      const tokenId = await tokenizeCard(instance, {
        card_number: cardNumber,
        holder_name: holderName,
        expiration_year: year,
        expiration_month: month,
        cvv2,
      });

      const result = await api.openpayCharge({
        ...payload,
        token_id: tokenId,
        device_session_id: sessionId,
        guardar_tarjeta: loggedIn && guardarTarjeta,
        http_context: browserHttpContext(),
        redirect_url: `${window.location.origin}/checkout`,
      });
      await resolverCargo(result);
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      onError(textoErrorPago(raw));
    } finally {
      setProcesando(false);
    }
  };

  const resolverCargo = async (result: {
    status: 'completed' | 'charge_pending';
    folio?: string;
    id_venta?: number;
    redirect_url?: string;
  }) => {
    if (result.status === 'charge_pending' && result.redirect_url) {
      window.location.href = result.redirect_url;
      return;
    }
    if (result.status === 'completed' && result.folio) {
      onSuccess({ folio: result.folio, id_venta: result.id_venta ?? 0 });
      return;
    }
    onError('El pago no se completó. El banco no confirmó el cargo. Intente de nuevo o use otra tarjeta.');
  };

  if (!config) {
    return <p className="text-sm text-surface-800/50">Verificando OpenPay...</p>;
  }

  if (!config.enabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>OpenPay aún no configurado.</strong>
        <p className="mt-1">
          Copie <code className="text-xs">openpay.local.php.example</code> como{' '}
          <code className="text-xs">openpay.local.php</code> y pegue Merchant ID, llave
          pública y privada del{' '}
          <a
            href="https://sandbox-dashboard.openpay.mx/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            dashboard de OpenPay
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-medium text-surface-500">Tarjetas aceptadas</p>
        <PaymentMarks />
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-surface-500">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Pago seguro vía Openpay. Los datos de la tarjeta no pasan por Alarcom.
        </p>
      </div>

      {loggedIn && tarjetas.length > 0 && (
        <div className="space-y-2">
          {tarjetas.map((t) => (
            <label
              key={t.id}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-surface-200 px-3 py-2 text-sm hover:border-brand-300"
            >
              <input
                type="radio"
                name="openpay-card"
                className="mt-1 h-4 w-4 border-surface-300 text-brand-600"
                checked={!usarNueva && tarjetaId === t.id}
                onChange={() => {
                  setTarjetaId(t.id);
                  setUsarNueva(false);
                }}
              />
              <span>
                <span className="font-medium text-surface-800">
                  {etiquetaMarcaTarjeta(t.marca)} •••• {t.terminacion}
                </span>
                <span className="mt-0.5 block text-xs text-surface-500">
                  Vence {t.mes}/{t.anio}
                  {t.titular ? ` · ${t.titular}` : ''}
                </span>
              </span>
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-surface-200 px-3 py-2 text-sm hover:border-brand-300">
            <input
              type="radio"
              name="openpay-card"
              className="h-4 w-4 border-surface-300 text-brand-600"
              checked={usarNueva}
              onChange={() => setUsarNueva(true)}
            />
            <span className="font-medium text-surface-800">Usar otra tarjeta</span>
          </label>
        </div>
      )}

      {mostrarFormularioNueva && (
        <>
          <input
            className="input-field"
            placeholder="Nombre en la tarjeta *"
            autoComplete="cc-name"
            value={holder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            onChange={(e) => setHolder(e.target.value)}
          />
          <input
            className="input-field"
            placeholder="Número de tarjeta *"
            inputMode="numeric"
            autoComplete="cc-number"
            value={number}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            onChange={(e) => setNumber(formatCardNumber(e.target.value))}
          />
          <div className="grid grid-cols-3 gap-3">
            <select
              className="input-field"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Mes de vencimiento"
            >
              <option value="">Mes</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="input-field"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              aria-label="Año de vencimiento"
            >
              <option value="">Año</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  20{y}
                </option>
              ))}
            </select>
            <input
              className="input-field"
              placeholder="CVV *"
              inputMode="numeric"
              autoComplete="cc-csc"
              maxLength={4}
              value={cvv}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              onChange={(e) => setCvv(digitsOnly(e.target.value).slice(0, 4))}
            />
          </div>
          {loggedIn ? (
            <label className="flex cursor-pointer items-start gap-3 text-sm text-surface-700">
              <input
                type="checkbox"
                checked={guardarTarjeta}
                onChange={(e) => setGuardarTarjeta(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-600"
              />
              <span>Guardar esta tarjeta para futuras compras. Podrá verla en su perfil.</span>
            </label>
          ) : (
            <p className="text-xs text-surface-500">
              Inicia sesión con tu cuenta Alarcom si quieres guardar la tarjeta para la próxima compra.
            </p>
          )}
        </>
      )}

      <button
        type="button"
        onClick={handlePay}
        disabled={procesando}
        className="btn-primary w-full"
      >
        <CreditCard className="h-4 w-4" />
        {procesando ? 'Procesando pago...' : 'Pagar con tarjeta'}
      </button>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">
          <p className="font-semibold">No se pudo completar el pago</p>
          <p className="mt-1">{error}</p>
          <p className="mt-2 text-xs text-red-700">
            Si el rechazo continúa, use otra tarjeta o llámenos al{' '}
            <a className="font-medium underline" href={CONTACTO.telefonoHref}>
              {CONTACTO.telefono}
            </a>
            .
          </p>
        </div>
      ) : cargaError ? (
        <p className="text-xs text-red-600">{cargaError}</p>
      ) : !formValid ? (
        <p className="text-xs text-amber-700">
          Faltan datos de envío
          {payload.requiere_factura ? ', RFC o razón social' : ''} o aceptar términos.
        </p>
      ) : null}
      <p className="text-[11px] leading-snug text-surface-500">
        {CONTACTO.ciudad} ·{' '}
        <a href={CONTACTO.telefonoHref} className="font-medium text-surface-700 hover:underline">
          {CONTACTO.telefono}
        </a>
        <br />
        {CONTACTO.direccion}
      </p>
    </div>
  );
}
